import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { supabase } from "./supabaseClient";

/**
 * Client identity and branding from the singleton `site_settings` row.
 *
 * Each client deployment has its own database, so nothing client-specific
 * lives in this file: when a value is missing we use neutral product
 * defaults. Loaded once by <SiteSettingsProvider> and read anywhere with
 * useSiteSettings().
 */

/** Product defaults, used when a client has not configured a value. */
export const DEFAULT_SITE_NAME = "Liga de fútbol";
export const DEFAULT_ACCENT_COLOR = "#34d399";

const LOGO_COLUMNS = "official_logo_url";
const BRANDING_COLUMNS =
  "site_name, short_name, tagline, accent_color, hero_image_url";
const AUTH_COLUMNS = "google_auth_enabled";

// Newest schema first. On a missing column (a database that has not run the
// latest migrations yet) we retry with the next, older column set.
const SELECT_LEVELS = [
  `${LOGO_COLUMNS}, ${BRANDING_COLUMNS}, ${AUTH_COLUMNS}`,
  `${LOGO_COLUMNS}, ${BRANDING_COLUMNS}`,
  LOGO_COLUMNS,
];

// v2: rows now include google_auth_enabled (v1 entries lack it).
const CACHE_KEY = "siteSettings:v2";
const FRESH_MS = 5 * 60_000; // re-check at most every 5 minutes per session

const HEX = /^#[0-9a-fA-F]{6}$/;

function readCache() {
  try {
    return JSON.parse(sessionStorage.getItem(CACHE_KEY) || "null");
  } catch {
    return null;
  }
}

function writeCache(raw) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ raw, ts: Date.now() }));
  } catch {
    // ignore quota / private mode
  }
}

const clean = (value) => {
  const text = typeof value === "string" ? value.trim() : "";
  return text || null;
};

const httpsUrl = (value) => {
  const url = clean(value);
  return url && /^https:\/\//i.test(url) ? url : null;
};

/** Turn a raw row (or null) into the values components use, with neutral defaults. */
export function resolveSiteSettings(raw) {
  const configuredName = clean(raw?.site_name);
  const configuredShortName = clean(raw?.short_name);
  const siteName = configuredName || DEFAULT_SITE_NAME;
  const accent = HEX.test(String(raw?.accent_color || "").trim())
    ? raw.accent_color.trim().toLowerCase()
    : DEFAULT_ACCENT_COLOR;

  return {
    siteName,
    shortName: configuredShortName || siteName,
    // No default tagline: when a client has none, it is simply hidden.
    tagline: clean(raw?.tagline),
    accentColor: accent,
    heroImageUrl: httpsUrl(raw?.hero_image_url),
    officialLogoUrl: httpsUrl(raw?.official_logo_url),
    hasConfiguredName: Boolean(configuredName),
    // Offer Google sign-in only when explicitly enabled. Missing column,
    // null, or a failed request all mean no.
    googleAuthEnabled: raw?.google_auth_enabled === true,
    // Browser-tab title from settings, or null to keep the build-time
    // <title> (REACT_APP_SITE_TITLE) from index.html.
    documentTitle: configuredShortName || configuredName,
  };
}

// PostgREST reports an unknown column as Postgres 42703 (undefined_column).
const isMissingColumnError = (error) =>
  error?.code === "42703" ||
  /column .* does not exist/i.test(error?.message || "");

async function fetchRawSiteSettings() {
  for (let level = 0; level < SELECT_LEVELS.length; level += 1) {
    const { data, error } = await supabase
      .from("site_settings")
      .select(SELECT_LEVELS[level])
      .eq("id", 1)
      .maybeSingle();

    if (!error) return data || {};
    const olderLevelLeft = level < SELECT_LEVELS.length - 1;
    if (!(olderLevelLeft && isMissingColumnError(error))) throw error;
  }
  return {};
}

// ---------------------------------------------------------------------------
// Accent: one setting drives the existing accent tokens in index.css.
// ---------------------------------------------------------------------------

function hexToRgb(hex) {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function toHex(rgb) {
  return `#${rgb
    .map((c) => Math.round(Math.max(0, Math.min(255, c))).toString(16).padStart(2, "0"))
    .join("")}`;
}

function relativeLuminance([r, g, b]) {
  const lin = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

function contrastRatio(a, b) {
  const [hi, lo] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

// The public site is dark (--bg-primary). The accent is also used for text
// and links, so a dark client color (e.g. navy) would be unreadable there.
const SITE_BACKGROUND = [7, 8, 12];
const MIN_TEXT_CONTRAST = 4.5;

/** Lighten toward white just enough to be readable on the dark background. */
export function readableAccent(hex) {
  const base = hexToRgb(hex);
  for (let mix = 0; mix <= 1; mix += 0.05) {
    const rgb = base.map((c) => c + (255 - c) * mix);
    if (contrastRatio(rgb, SITE_BACKGROUND) >= MIN_TEXT_CONTRAST) return toHex(rgb);
  }
  return "#ffffff";
}

const ACCENT_TOKENS = [
  "--accent",
  "--accent-rgb",
  "--accent-readable",
  "--accent-strong",
  "--accent-contrast",
];

const DARK_INK = "#04140f";
const LIGHT_INK = "#ffffff";

/** Text color for accent-filled surfaces: whichever reads better on both fills. */
function contrastInk(fills) {
  const worst = (ink) =>
    Math.min(...fills.map((fill) => contrastRatio(hexToRgb(ink), fill)));
  return worst(DARK_INK) >= worst(LIGHT_INK) ? DARK_INK : LIGHT_INK;
}

/**
 * Set the accent tokens on <html>. The product default keeps the values
 * already defined in index.css, so the default look is byte-for-byte the same.
 *
 * --accent is always the exact configured brand color; only the text token
 * (--accent-readable) is adjusted, and only when the brand color is too dark.
 */
export function applyAccentColor(hex, root = document.documentElement) {
  if (!root) return;
  if (!HEX.test(hex || "") || hex.toLowerCase() === DEFAULT_ACCENT_COLOR) {
    ACCENT_TOKENS.forEach((token) => root.style.removeProperty(token));
    return;
  }
  const accent = hex.toLowerCase();
  const rgb = hexToRgb(accent);
  // Filled buttons use a slightly deeper shade of the brand color.
  const strongRgb = rgb.map((c) => c * 0.82);

  root.style.setProperty("--accent", accent);
  root.style.setProperty("--accent-rgb", rgb.join(", "));
  root.style.setProperty("--accent-readable", readableAccent(accent));
  root.style.setProperty("--accent-strong", toHex(strongRgb));
  root.style.setProperty("--accent-contrast", contrastInk([rgb, strongRgb]));
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

const SiteSettingsContext = createContext(null);

export function SiteSettingsProvider({ children }) {
  // `raw` is null until the first successful load (or a session cache hit).
  const [raw, setRaw] = useState(() => readCache()?.raw ?? null);
  const [loaded, setLoaded] = useState(() => Boolean(readCache()));

  useEffect(() => {
    const entry = readCache();
    if (entry && Date.now() - entry.ts < FRESH_MS) return undefined;

    let cancelled = false;
    fetchRawSiteSettings()
      .then((next) => {
        if (cancelled) return;
        writeCache(next);
        setRaw(next);
      })
      .catch((err) => {
        // A settings outage must not break the site: keep the cached values
        // if there are any, otherwise fall back to neutral defaults.
        console.error("Failed to load site settings:", err);
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(
    () => ({ loaded, ...resolveSiteSettings(raw) }),
    [loaded, raw]
  );

  useEffect(() => {
    if (!loaded) return;
    applyAccentColor(value.accentColor);
    if (value.documentTitle) document.title = value.documentTitle;
  }, [loaded, value.accentColor, value.documentTitle]);

  return React.createElement(SiteSettingsContext.Provider, { value }, children);
}

/** Current client settings; `loaded` is false until the first load finishes. */
export function useSiteSettings() {
  const ctx = useContext(SiteSettingsContext);
  if (!ctx) {
    // Outside the provider (tests, isolated renders): neutral defaults.
    return { loaded: true, ...resolveSiteSettings(null) };
  }
  return ctx;
}
